import * as THREE from "three";
import type { PlayerId, Vector3Like } from "@ict/shared";
import { CharacterModel } from "./CharacterModel";

interface RemotePlayer {
  model: CharacterModel;
  from: THREE.Vector3;
  to: THREE.Vector3;
  fromRot: number;
  toRot: number;
  t: number;
  nameSprite: THREE.Sprite;
}

const INTERP_DURATION = 0.12; // matches roughly one network tick

function makeNameSprite(name: string): THREE.Sprite {
  const canvas = document.createElement("canvas");
  canvas.width = 256;
  canvas.height = 64;
  const ctx = canvas.getContext("2d")!;
  ctx.font = "bold 34px Segoe UI";
  ctx.fillStyle = "#9ff2ff";
  ctx.textAlign = "center";
  ctx.shadowColor = "#35e6ff";
  ctx.shadowBlur = 12;
  ctx.fillText(name.toUpperCase(), 128, 42);
  const texture = new THREE.CanvasTexture(canvas);
  const material = new THREE.SpriteMaterial({ map: texture, depthTest: false, transparent: true });
  const sprite = new THREE.Sprite(material);
  sprite.scale.set(1.6, 0.4, 1);
  sprite.position.y = 2.15;
  return sprite;
}

/** Manages other players' visible avatars, smoothing their networked positions between updates. */
export class RemotePlayerManager {
  private players = new Map<PlayerId, RemotePlayer>();

  constructor(private readonly scene: THREE.Scene) {}

  upsert(playerId: PlayerId, name: string, position: Vector3Like, rotationY: number): void {
    let rp = this.players.get(playerId);
    if (!rp) {
      const model = new CharacterModel("/models/player-remote.glb");
      const nameSprite = makeNameSprite(name);
      model.root.add(nameSprite);
      model.root.position.set(position.x, position.y, position.z);
      model.root.rotation.y = rotationY;
      this.scene.add(model.root);
      rp = {
        model,
        from: new THREE.Vector3(position.x, position.y, position.z),
        to: new THREE.Vector3(position.x, position.y, position.z),
        fromRot: rotationY,
        toRot: rotationY,
        t: 1,
        nameSprite,
      };
      this.players.set(playerId, rp);
    }
  }

  applyMove(playerId: PlayerId, position: Vector3Like, rotationY: number): void {
    const rp = this.players.get(playerId);
    if (!rp) return;
    rp.from.copy(rp.model.root.position);
    rp.to.set(position.x, position.y, position.z);
    rp.fromRot = rp.model.root.rotation.y;
    rp.toRot = rotationY;
    rp.t = 0;
  }

  remove(playerId: PlayerId): void {
    const rp = this.players.get(playerId);
    if (!rp) return;
    rp.model.dispose();
    this.scene.remove(rp.model.root);
    this.players.delete(playerId);
  }

  update(dt: number): void {
    for (const rp of this.players.values()) {
      if (rp.t < 1) {
        rp.t = Math.min(1, rp.t + dt / INTERP_DURATION);
        rp.model.root.position.lerpVectors(rp.from, rp.to, rp.t);
        rp.model.root.rotation.y = lerpAngle(rp.fromRot, rp.toRot, rp.t);
        const speed = rp.from.distanceTo(rp.to) / INTERP_DURATION;
        rp.model.setState(speed > 4 ? "run" : speed > 0.3 ? "walk" : "idle");
      }
      rp.model.update(dt);
    }
  }

  dispose(): void {
    for (const playerId of [...this.players.keys()]) this.remove(playerId);
  }
}

function lerpAngle(a: number, b: number, t: number): number {
  let diff = b - a;
  diff = ((diff + Math.PI) % (Math.PI * 2)) - Math.PI;
  return a + diff * t;
}
